import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MessageCircle, ScrollText, Clock, XCircle, Pause, Play } from "lucide-react";
import { StatusFilter, filterItems } from "@/components/StatusFilter";
import { CampaignSearch } from "@/components/CampaignSearch";

const STATUS_COLORS = {
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  running: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
  queued: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  scheduled: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  paused: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  cancelled: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  error: "bg-rose-500/15 text-rose-400 border-rose-500/30",
};

const FIELD_OPTIONS = [
  { key: "name", label: "Name", locked: true },
  { key: "phone", label: "Phone Number", locked: true },
  { key: "email", label: "Email" },
  { key: "product_name", label: "Product Name" },
  { key: "city", label: "City" },
  { key: "pin_code", label: "Pin Code" },
  { key: "date", label: "Purchase Date" },
];

function CreateDialog({ open, onClose, audiences, onCreated }) {
  const [name, setName] = useState("");
  const [audienceId, setAudienceId] = useState("");
  const [fields, setFields] = useState(["name", "phone"]);
  const [templateId, setTemplateId] = useState("");
  const [busy, setBusy] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");

  const toggle = (k) => setFields(fields.includes(k) ? fields.filter((f) => f !== k) : [...fields, k]);

  const run = async () => {
    if (!name.trim()) return toast.error("Name the campaign");
    if (!audienceId) return toast.error("Pick an audience");
    if (!templateId.trim()) return toast.error("Enter the template identifier");
    let scheduled_at = null;
    if (scheduleAt) {
      const dt = new Date(scheduleAt);
      if (dt <= new Date()) return toast.error("Scheduled time must be in the future");
      scheduled_at = dt.toISOString();
    }
    setBusy(true);
    try {
      const { data } = await api.post("/wa-campaigns", { name, audience_id: audienceId, fields, template_identifier: templateId.trim(), scheduled_at });
      toast.success(scheduled_at ? `Campaign scheduled for ${new Date(scheduled_at).toLocaleString()}` : "WhatsApp campaign started");
      if (!scheduled_at) onCreated(data.id);
      onClose();
      setName(""); setAudienceId(""); setScheduleAt(""); setTemplateId("");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle className="font-heading">New WhatsApp campaign</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input data-testid="wa-campaign-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Campaign name" className="bg-slate-900 border-slate-700 text-slate-100" />
          <div>
            <Input data-testid="wa-template-id-input" value={templateId} onChange={(e) => setTemplateId(e.target.value)} placeholder="Template identifier (e.g. new_launch_offer_v2)" className="bg-slate-900 border-slate-700 text-slate-100" />
            <p className="text-[11px] text-slate-500 mt-1">Sent as <code className="font-mono text-slate-400">template_identifier</code> in every webhook payload — use it in Pabbly to pick the WhatsApp template.</p>
          </div>
          <Select value={audienceId} onValueChange={setAudienceId}>
            <SelectTrigger data-testid="wa-audience-select" className="bg-slate-900 border-slate-700 text-slate-200"><SelectValue placeholder="Choose audience…" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
              {audiences.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Variables sent to Pabbly</p>
            <div className="grid grid-cols-2 gap-2">
              {FIELD_OPTIONS.map((f) => (
                <label key={f.key} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${fields.includes(f.key) ? "border-emerald-500/40 bg-emerald-500/5 text-slate-200" : "border-slate-800 text-slate-400"} ${f.locked ? "opacity-80" : "cursor-pointer"}`}>
                  <input data-testid={`wa-field-${f.key}`} type="checkbox" disabled={f.locked} checked={fields.includes(f.key)} onChange={() => toggle(f.key)} />
                  {f.label}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-2">One JSON payload per contact is POSTed to your Pabbly webhook. Map these variables to your WhatsApp template inside Pabbly.</p>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Schedule (optional)</p>
            <Input data-testid="wa-schedule-datetime-input" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="bg-slate-900 border-slate-700 text-slate-100" />
            <p className="text-[10px] text-slate-500 mt-1">Leave empty to run now. Dispatched within ~15 minutes of the scheduled time.</p>
          </div>
          <Button data-testid="whatsapp-run-button" onClick={run} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
            {busy ? "Starting…" : scheduleAt ? "Schedule campaign" : "Run campaign"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LogsDialog({ campaignId, onClose }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!campaignId) return;
    const load = () => api.get(`/wa-campaigns/${campaignId}`).then((r) => setDetail(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [campaignId]);

  return (
    <Dialog open={!!campaignId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{detail?.name || "Campaign log"}</DialogTitle></DialogHeader>
        {detail && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Badge className={`border ${STATUS_COLORS[detail.status] || ""}`}>{detail.status}</Badge>
              <span className="text-xs font-mono text-slate-400">{detail.sent} pushed{detail.failed ? ` · ${detail.failed} failed` : ""} / {detail.total}</span>
            </div>
            {detail.error && <p className="text-sm text-rose-400">{detail.error}</p>}
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {(detail.logs || []).map((l, i) => (
                <div key={i} data-testid="wa-log-row" className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-xs">
                  <span className="text-slate-300">{l.name || "—"} <span className="font-mono text-slate-500">{l.phone}</span></span>
                  {l.status === "sent" ? <span className="text-emerald-400 font-mono">✓ pushed</span> : <span className="text-rose-400 font-mono" title={l.error}>✗ failed</span>}
                </div>
              ))}
              {!detail.logs?.length && <p className="text-xs text-slate-500">No log entries yet…</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function WhatsAppPage() {
  const [items, setItems] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [logsId, setLogsId] = useState(null);

  const load = () => api.get("/wa-campaigns").then((r) => setItems(r.data)).catch(() => setItems([]));

  const cancel = async (id) => {
    try {
      await api.post(`/wa-campaigns/${id}/cancel`);
      toast.success("Campaign cancelled");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const pause = async (id) => {
    try {
      await api.post(`/wa-campaigns/${id}/pause`);
      toast.success("Campaign paused");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const resume = async (id) => {
    try {
      await api.post(`/wa-campaigns/${id}/resume`);
      toast.success("Campaign resumed");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  useEffect(() => {
    load();
    api.get("/audiences").then((r) => setAudiences(r.data)).catch(() => {});
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Pushed to your Pabbly webhook, one contact at a time</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">WhatsApp Campaigns</h1>
        </div>
        <Button data-testid="new-wa-campaign-button" onClick={() => setCreateOpen(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white">
          <Plus size={15} className="mr-1.5" /> New campaign
        </Button>
      </div>

      {items === null ? null : items.length === 0 ? (
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-12 text-center" data-testid="wa-campaigns-empty">
          <MessageCircle size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">No WhatsApp campaigns yet. Pick an audience and push it through Pabbly.</p>
        </div>
      ) : (
        <>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <StatusFilter items={items} value={filter} onChange={setFilter} accent="emerald" />
          <CampaignSearch value={search} onChange={setSearch} testId="wa-campaign-search" />
        </div>
        {filterItems(items, filter, search).length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center" data-testid="wa-campaigns-filter-empty">No campaigns match{search ? ` "${search}"` : " this status"}.</p>
        ) : (
        <div className="space-y-3">
          {filterItems(items, filter, search).map((c) => {
            const pct = c.total ? Math.round(((c.sent + c.failed) / c.total) * 100) : 0;
            return (
              <div key={c.id} data-testid="wa-campaign-row" className="stat-card p-5 fade-up">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <div>
                    <div className="font-heading font-semibold text-slate-100">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.audience_name} · vars: {(c.fields || []).join(", ")}{c.template_identifier ? <> · template: <span className="font-mono text-slate-400">{c.template_identifier}</span></> : null} · {new Date(c.created_at).toLocaleString()}</div>
                    {c.status === "scheduled" && c.scheduled_at && (
                      <div data-testid="wa-campaign-scheduled-time" className="text-xs text-blue-400 flex items-center gap-1 mt-0.5">
                        <Clock size={11} /> Scheduled for {new Date(c.scheduled_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`border ${STATUS_COLORS[c.status] || ""}`} data-testid="wa-campaign-status">{c.status}</Badge>
                    {["queued", "running"].includes(c.status) && (
                      <Button data-testid="wa-campaign-pause-button" size="sm" variant="outline" onClick={() => pause(c.id)} className="border-slate-700 text-sky-400 hover:bg-slate-800 h-8">
                        <Pause size={13} className="mr-1.5" /> Pause
                      </Button>
                    )}
                    {["paused", "cancelled"].includes(c.status) && (
                      <Button data-testid="wa-campaign-resume-button" size="sm" onClick={() => resume(c.id)} className="bg-emerald-500 hover:bg-emerald-600 text-white h-8">
                        <Play size={13} className="mr-1.5" /> Resume
                      </Button>
                    )}
                    {["scheduled", "queued", "running", "paused"].includes(c.status) && (
                      <Button data-testid="wa-campaign-cancel-button" size="sm" variant="outline" onClick={() => cancel(c.id)} className="border-slate-700 text-rose-400 hover:bg-slate-800 h-8">
                        <XCircle size={13} className="mr-1.5" /> {c.status === "scheduled" ? "Cancel" : "Stop"}
                      </Button>
                    )}
                    {c.status !== "scheduled" && (
                      <Button data-testid="wa-campaign-logs-button" size="sm" variant="outline" onClick={() => setLogsId(c.id)} className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8">
                        <ScrollText size={13} className="mr-1.5" /> Log
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Progress value={pct} className="h-1.5 bg-slate-800 flex-1" />
                  <span className="text-xs font-mono text-slate-400 shrink-0" data-testid="wa-campaign-progress">{c.sent} pushed{c.failed ? ` · ${c.failed} failed` : ""} / {c.total}</span>
                </div>
                {c.error && <div data-testid="wa-campaign-error" className="mt-2 text-xs text-rose-400">{c.error}</div>}
                {c.recoveries > 0 && (
                  <div data-testid="wa-campaign-recovered" className="mt-1 text-[11px] text-amber-400/90">Auto-recovered {c.recoveries}× after interruption — continued from last pushed contact</div>
                )}
              </div>
            );
          })}
        </div>
        )}
        </>
      )}

      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} audiences={audiences} onCreated={(id) => setLogsId(id)} />
      <LogsDialog campaignId={logsId} onClose={() => setLogsId(null)} />
    </div>
  );
}
